/**
 * Adhoc Interest Rate Change Dialog
 *
 * Lets a user request a per-contract interest rate change on an Active
 * contract. The rate change is applied with an old-rate stub from the last
 * interest payment up to the day before the effective date, then the
 * post-effective slice is regenerated at the new rate.
 *
 * Submit creates a 'Pending'/origin='adhoc' contract_rate_changes row plus a
 * pending_changes row (change_type='adhoc_rate_change'). The schedule changes
 * only after an approver acts on the pending change.
 */

import React, { useState, useEffect, useRef } from 'react';
import { TextField, Grid, InputAdornment, Typography } from '@mui/material';
import { format } from 'date-fns';
import { apiPost } from '../../utils/apiClient';
import logger from '../../utils/logger';
import ContractActionDialogBase from './ContractActionDialogBase';
import RateChangePreviewModal from './RateChangePreviewModal';

const AdhocRateChangeDialog = ({ open, onClose, contract, contractId, onSuccess }) => {
  const cid = contractId || contract?.id;
  const currentRate = contract?.interest_rate != null ? parseFloat(contract.interest_rate) : null;

  const [formData, setFormData] = useState({
    new_rate: '',
    effective_date: format(new Date(), 'yyyy-MM-dd'),
  });
  const [motivation, setMotivation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRefreshing, setPreviewRefreshing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // Monotonic id used to discard responses from older auto-refresh requests
  // that resolve after a newer one has already overwritten previewData.
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    if (open) {
      setFormData({
        new_rate: '',
        effective_date: format(new Date(), 'yyyy-MM-dd'),
      });
      setMotivation('');
      setError(null);
      setPreviewData(null);
      setPreviewModalOpen(false);
      setPreviewRefreshing(false);
    }
  }, [open]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // We deliberately don't clear previewData here — the existing preview
    // stays visible while the auto-refresh (on blur) is in flight.
  };

  const isRateValid = (() => {
    const v = parseFloat(formData.new_rate);
    if (!Number.isFinite(v) || v <= 0) return false;
    if (currentRate != null && Math.abs(v - currentRate) < 0.0001) return false;
    return true;
  })();

  const isValid = !!formData.effective_date && isRateValid && !!motivation;

  // Fetch the preview from the same endpoint used by the manual Preview
  // button. If `mode = 'refresh'`, we treat it as a background refresh and
  // skip the open-modal side effect.
  const fetchPreview = async (mode = 'manual') => {
    if (!cid) return;
    if (!isRateValid || !formData.effective_date) return;

    const reqId = ++latestRequestIdRef.current;
    if (mode === 'manual') {
      setPreviewLoading(true);
      setError(null);
    } else {
      setPreviewRefreshing(true);
    }
    try {
      const response = await apiPost(`/contracts/${cid}/rate-change-preview`, {
        new_rate: parseFloat(formData.new_rate),
        effective_date: formData.effective_date,
      });
      // Drop the result if a newer request has been issued since.
      if (reqId !== latestRequestIdRef.current) return;
      if (response?.success && response.data) {
        setPreviewData(response.data);
        if (mode === 'manual') setPreviewModalOpen(true);
      } else if (mode === 'manual') {
        setError(response?.message || 'Failed to generate preview');
      }
    } catch (err) {
      if (reqId !== latestRequestIdRef.current) return;
      if (mode === 'manual') {
        setError('Failed to generate preview: ' + (err.response?.data?.message || err.message));
      } else {
        // Background refresh errors are non-blocking — log only.
        logger.warn('Adhoc rate change preview refresh failed', { message: err.message });
      }
    } finally {
      if (reqId !== latestRequestIdRef.current) return;
      if (mode === 'manual') setPreviewLoading(false);
      else setPreviewRefreshing(false);
    }
  };

  const handlePreview = async () => {
    if (!cid) return;
    if (!isRateValid) {
      setError(currentRate != null && Math.abs(parseFloat(formData.new_rate) - currentRate) < 0.0001
        ? 'New rate is identical to the current rate'
        : 'Enter a valid new rate (> 0)');
      return;
    }
    if (!formData.effective_date) {
      setError('Effective date is required');
      return;
    }
    await fetchPreview('manual');
  };

  // Fired on blur of the rate or date inputs. Only refresh if the preview
  // modal is already open and inputs are valid — don't auto-open the modal
  // from a blur.
  const handleAutoRefresh = () => {
    if (!previewModalOpen) return;
    if (!isRateValid || !formData.effective_date) return;
    fetchPreview('refresh');
  };

  const handleSubmit = async ({ proof_document_ids }) => {
    if (!isValid) {
      setError('All fields are required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiPost(`/contracts/${cid}/rate-change`, {
        new_rate: parseFloat(formData.new_rate),
        effective_date: formData.effective_date,
        // Motivation does double duty: the rate-change record stores it as the
        // event reason; the pending_changes record stores it as the approver-
        // facing motivation. Splitting them in the UI was confusing.
        reason: motivation,
        motivation,
        proof_document_ids,
      });
      if (response?.success) {
        onClose();
        if (onSuccess) onSuccess();
      } else {
        setError(response?.message || 'Failed to submit rate change request');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit rate change request');
    } finally {
      setSubmitting(false);
    }
  };

  const description = currentRate != null
    ? `Change the interest rate on this contract from ${currentRate}% effective on a chosen date. The schedule will be regenerated upon approval, with a stub period at the current rate from the last interest payment up to the day before the effective date.`
    : 'Change the interest rate on this contract from a chosen effective date. The schedule will be regenerated upon approval.';

  return (
    <>
      <ContractActionDialogBase
        open={open}
        onClose={onClose}
        contract={contract}
        title="Adhoc Interest Rate Change"
        description={description}
        submitLabel="Submit for Approval"
        submitColor="info"
        isValid={isValid}
        submitting={submitting}
        error={error}
        onClearError={() => setError(null)}
        onSubmit={handleSubmit}
        onPreview={handlePreview}
        showPreview={true}
        previewLoading={previewLoading}
        reason={motivation}
        onReasonChange={setMotivation}
        reasonLabel="Motivation (Required)"
        reasonPlaceholder="Why is this rate change being made? This will be reviewed by an approver."
        reasonHelperText="A motivation is mandatory for rate change amendments on active contracts."
      >
        <Grid container spacing={2}>
          <Grid size={6}>
            <TextField
              label="New Interest Rate"
              type="number"
              value={formData.new_rate}
              onChange={(e) => handleFieldChange('new_rate', e.target.value)}
              onBlur={handleAutoRefresh}
              fullWidth
              required
              inputProps={{ step: '0.0001', min: 0 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText={currentRate != null ? `Current rate: ${currentRate}%` : ' '}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Effective Date"
              type="date"
              value={formData.effective_date}
              onChange={(e) => handleFieldChange('effective_date', e.target.value)}
              onBlur={handleAutoRefresh}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: contract?.funds_received_date?.substring(0, 10) || contract?.deposit_date?.substring(0, 10),
                max: contract?.maturity_date?.substring(0, 10),
              }}
              helperText="Rate applies from this date forward."
            />
          </Grid>
          {currentRate != null && isRateValid && (
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary">
                {parseFloat(formData.new_rate) > currentRate
                  ? `Increasing rate by ${(parseFloat(formData.new_rate) - currentRate).toFixed(4)}% from ${formData.effective_date}.`
                  : `Decreasing rate by ${(currentRate - parseFloat(formData.new_rate)).toFixed(4)}% from ${formData.effective_date}.`}
              </Typography>
            </Grid>
          )}
        </Grid>
      </ContractActionDialogBase>
      <RateChangePreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        previewData={previewData}
        showApprove={false}
        refreshing={previewRefreshing}
        currencyCode={contract?.currency_code || 'ZAR'}
      />
    </>
  );
};

export default AdhocRateChangeDialog;
